import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { ConfirmationModal, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useLocaleKeys } from '~/i18n';
import { ClientErrorResponse } from '~/infra/error-response/ClientErrorResponse';
import { errorTextsPath } from '~/infra/error-response/ErrorResponse';
import { clientStateKey } from '~/pages/ClientsPage/ClientsPage.utils';
export const GetClientArchiveRestoreDeleteTID = ()=>getTestIds();
export function useClientArchiveRestoreDelete() {
    let { onComplete = noop, onDeleteComplete = noop } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    const { api } = useStoreActions();
    const queryClient = useQueryClient();
    const invalidate = (queryKey)=>{
        void queryClient.invalidateQueries({
            queryKey
        });
    };
    const { showModal, showErrorToast } = useOverlayContext();
    const { t: clientsDashboardTexts } = useLocaleKeys('client.dashboard');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const { ClientArchiveRestoreDeleteTestIds: TID } = GetClientArchiveRestoreDeleteTID();
    const archiveMutation = useMutationState({
        mutationFn: (clientId)=>api.client.archive(clientId),
        withPageLoader: true,
        mutationName: 'archiveClient',
        toasts: {
            successMessage: clientsDashboardTexts.archiveSuccessToast(),
            successTestId: TID.ArchiveSuccessToast()
        },
        onSuccess: (_result, clientId)=>{
            invalidate([
                QueryKeys.Clients
            ]);
            return onComplete({
                clientId,
                status: 'archived'
            });
        },
        onError: (err)=>showErrorToast(new ClientErrorResponse(err).archiveMessage(errorTexts), TID.ArchiveErrorToast())
    });
    const restoreMutation = useMutationState({
        mutationFn: (clientId)=>api.client.restore(clientId),
        withPageLoader: true,
        mutationName: 'restoreClient',
        toasts: {
            successMessage: clientsDashboardTexts.restoreSuccessToast(),
            successTestId: TID.RestoreSuccessToast()
        },
        onSuccess: (_result, clientId)=>{
            invalidate([
                QueryKeys.Clients
            ]);
            return onComplete({
                clientId,
                status: 'active'
            });
        },
        onError: (err)=>showErrorToast(new ClientErrorResponse(err).restoreMessage(errorTexts), TID.RestoreErrorToast())
    });
    const deleteMutation = useMutationState({
        mutationFn: (clientId)=>api.client.delete(clientId),
        withPageLoader: true,
        mutationName: 'deleteClient',
        toasts: {
            successMessage: clientsDashboardTexts.deleteSuccessToast(),
            successTestId: TID.DeleteSuccessToast()
        },
        onSuccess: (_result, clientId)=>{
            invalidate([
                QueryKeys.Clients
            ]);
            invalidate([
                QueryKeys.Contacts
            ]);
            return onDeleteComplete(clientId);
        },
        onError: (err)=>showErrorToast(new ClientErrorResponse(err).deleteMessage(errorTexts), TID.DeleteErrorToast())
    });
    const onRestore = async (client)=>{
        restoreMutation.mutate(client.id);
    };
    const onArchive = async (client)=>{
        if (client.isSample || client.archiveEligibility.type === 'notEligible') return;
        const stateKey = clientStateKey(client.archiveEligibility);
        const confirmed = await showModal({
            biName: 'clients_dashboard_archive_confirmation_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'clients_archive_confirm'
                        },
                        secondaryButton: {
                            name: 'clients_archive_cancel'
                        }
                    },
                    skin: "danger",
                    textAlignCenter: false,
                    title: clientsDashboardTexts.archiveConfirmation.title[stateKey](),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: clientsDashboardTexts.archiveConfirmation.content[stateKey]({
                            clientName: client.name
                        })
                    }),
                    primaryButtonLabel: clientsDashboardTexts.archiveConfirmation.primaryButton(),
                    secondaryButtonLabel: clientsDashboardTexts.archiveConfirmation.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        archiveMutation.mutate(client.id);
    };
    const onDelete = async (client)=>{
        if (client.isSample || client.deleteEligibility.type === 'notEligible') return;
        const stateKey = clientStateKey(client.deleteEligibility);
        const confirmed = await showModal({
            biName: 'clients_dashboard_delete_confirmation_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'clients_delete_confirm'
                        },
                        secondaryButton: {
                            name: 'clients_delete_cancel'
                        }
                    },
                    skin: "danger",
                    textAlignCenter: false,
                    title: clientsDashboardTexts.deleteConfirmation.title[stateKey](),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        showWhiteSpaces: true,
                        children: clientsDashboardTexts.deleteConfirmation.content[stateKey]({
                            clientName: client.name
                        })
                    }),
                    primaryButtonLabel: clientsDashboardTexts.deleteConfirmation.primaryButton(),
                    secondaryButtonLabel: clientsDashboardTexts.deleteConfirmation.secondaryButton(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    onCloseButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        deleteMutation.mutate(client.id);
    };
    return {
        onArchive,
        onRestore,
        onDelete
    };
}
